import { zodResolver } from "@hookform/resolvers/zod";
import { type CreateCommunityStep1Payload, createCommunityStep1Schema, slugify } from "@upsquad/shared";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Button, FieldError, Input, Label, TextArea, TextField } from "@/src/common/components/ui";
import { useSlugCheck } from "@/src/common/hooks/use-slug-check";
import { getDomainSuffix } from "@/src/common/lib/subdomain";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { CommunityPreviewCard } from "@/src/onboarding/components/community-preview-card";
import { OnboardingLayout } from "@/src/onboarding/components/onboarding-layout";
import { ONBOARDING_ROUTES } from "@/src/onboarding/constants/onboarding-routes";
import { getCreateCommunityDraft } from "@/src/onboarding/store/onboarding-selectors";
import { setCreateCommunityStep1 } from "@/src/onboarding/store/onboarding-slice";

export default function CreateCommunityProfilePage() {
	const { t } = useTranslation("onboarding");
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const draft = useAppSelector(getCreateCommunityDraft);
	const slugManuallyEdited = useRef(!!draft?.slug);

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<CreateCommunityStep1Payload>({
		resolver: zodResolver(createCommunityStep1Schema),
		mode: "onTouched",
		defaultValues: {
			name: draft?.name ?? "",
			slug: draft?.slug ?? "",
			description: draft?.description ?? "",
		},
	});

	const watchName = watch("name");
	const watchSlug = watch("slug");
	const { isAvailable, isUnavailable, isChecking, isValid } = useSlugCheck(watchSlug);
	const domainSuffix = getDomainSuffix();

	useEffect(() => {
		if (!slugManuallyEdited.current) {
			const generated = slugify(watchName);
			setValue("slug", generated, { shouldValidate: true });
		}
	}, [watchName, setValue]);

	const onSubmit = (data: CreateCommunityStep1Payload) => {
		if (!isAvailable) return;
		dispatch(setCreateCommunityStep1(data));
		navigate(ONBOARDING_ROUTES.CREATE_COMMUNITY_PERSONALIZE);
	};

	const slugBorderColor = (() => {
		if (!isValid || watchSlug.length < 3 || isChecking) return "border-default-300";
		if (isAvailable) return "border-success";
		if (isUnavailable) return "border-danger";
		return "border-default-300";
	})();

	return (
		<>
			<PageHelmet title="Create Community" />
			<OnboardingLayout leftContent={<CommunityPreviewCard name={watchName} slug={watchSlug} />} step={2} showBack>
				<h1 className="text-2xl font-bold mb-2">{t("create_community_title")}</h1>
				<p className="text-sm text-foreground/50 mb-8">{t("create_community_subtitle")}</p>

				<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
					<Controller
						name="name"
						control={control}
						render={({ field }) => (
							<TextField isInvalid={!!errors.name} isRequired>
								<Label>{t("community_name_label")}</Label>
								<Input
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									name={field.name}
									placeholder={t("community_name_placeholder")}
								/>
								{errors.name?.message && <FieldError>{errors.name.message}</FieldError>}
							</TextField>
						)}
					/>

					<Controller
						name="slug"
						control={control}
						render={({ field }) => (
							<TextField isInvalid={!!errors.slug || isUnavailable}>
								<Label>{t("slug_label")}</Label>
								<div className="flex items-center gap-2">
									<div className={`flex-1 relative ${slugBorderColor} rounded-lg`}>
										<Input
											value={field.value}
											onChange={(e) => {
												slugManuallyEdited.current = true;
												field.onChange(e);
											}}
											onBlur={field.onBlur}
											name={field.name}
											className={`w-full border-2 ${slugBorderColor}`}
										/>
										{isChecking && isValid && watchSlug.length >= 3 && (
											<div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
												<Loader2 className="size-4 text-foreground/40 animate-spin" />
											</div>
										)}
									</div>
									<span className="text-sm text-foreground/40 shrink-0">{domainSuffix}</span>
								</div>
								{errors.slug?.message && <FieldError>{errors.slug.message}</FieldError>}
								{isUnavailable && !errors.slug && <FieldError>{t("slug_unavailable")}</FieldError>}
							</TextField>
						)}
					/>

					<Controller
						name="description"
						control={control}
						render={({ field }) => (
							<TextField isInvalid={!!errors.description}>
								<Label>{t("description_label")}</Label>
								<TextArea
									value={field.value ?? ""}
									onChange={field.onChange}
									onBlur={field.onBlur}
									name={field.name}
									placeholder={t("description_placeholder")}
									rows={3}
								/>
								{errors.description?.message && <FieldError>{errors.description.message}</FieldError>}
							</TextField>
						)}
					/>

					<Button type="submit" variant="primary" fullWidth isDisabled={!isAvailable} className="mt-2">
						{t("next")}
					</Button>
				</form>
			</OnboardingLayout>
		</>
	);
}
